import React, { useMemo } from 'react'

import GenericSelectionModal, { SelectionOption } from '../common/select-component/GenericSelectionModal'
import GenericMultiSelectDialog, { MultiSelectItem } from '../common/select-component/GenericMultiSelectDialog'
import { SegmentInclusionListType, SegmentInclusionType } from 'src/lib/types/segment'
import { useLocale } from 'src/lib/hooks/useLocal'
import { IconButton } from '@shopflo/ui'
import { MapPinLine, X } from '@phosphor-icons/react'

interface StateFilterItemProps {
  filter: SegmentInclusionListType
  index: number
  isFirst: boolean
  hasFileAbove: boolean
  indianStates: MultiSelectItem[]
  inclusionList: SegmentInclusionListType[]
  hasError?: boolean
  onModeChange: (index: number, mode: SegmentInclusionType) => void
  onStateChange: (index: number, selectedKeys: string[]) => void
  onRemove: (index: number) => void
}

const StateFilterItem: React.FC<StateFilterItemProps> = ({
  filter,
  index,
  isFirst,
  hasFileAbove,
  indianStates,
  inclusionList,
  hasError = false,
  onModeChange,
  onStateChange,
  onRemove
}) => {
  const { t } = useLocale()

  const ALL_INCLUSION_OPTIONS: SelectionOption[] = [
    { key: 'INCLUDE', value: t('segment_creation.include') },
    { key: 'EXCLUDE', value: t('segment_creation.include_all_except') }
  ]

  // Get modes already used by other STATE items (excluding current item)
  const usedModesByOthers = useMemo(() => {
    return inclusionList
      .filter((item, idx) => item.item === 'STATE' && idx !== index)
      .map(item => item.type)
  }, [inclusionList, index])

  // Filter inclusion options: only show options not used by other STATE items
  const availableInclusionOptions = useMemo(() => {
    return ALL_INCLUSION_OPTIONS.filter(
      option => option.key === filter.type || !usedModesByOthers.includes(option.key as SegmentInclusionType)
    )
  }, [filter.type, usedModesByOthers])

  // Get states already selected by other STATE items (excluding current item)
  const statesUsedByOthers = useMemo(() => {
    const otherStateFilters = inclusionList.filter(
      (item, idx) => item.item === 'STATE' && idx !== index
    )
    return new Set(otherStateFilters.flatMap(item => item.values))
  }, [inclusionList, index])

  // Modify indianStates to disable already selected states
  const statesWithDisabled = useMemo(() => {
    return indianStates.map(state => ({
      ...state,
      disabled: statesUsedByOthers.has(state.key)
    }))
  }, [indianStates, statesUsedByOthers])

  const pillClass = 'flex whitespace-nowrap cursor-pointer items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-zinc-600 transition-colors bg-zinc-950/5 hover:bg-zinc-950/8'
  const pillErrorClass = 'flex whitespace-nowrap cursor-pointer items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-red-600 transition-colors bg-red-600/10 hover:bg-red-600/15'

  const getSelectedStatesDisplay = useMemo(() => {
    if (filter.values.length === 0)
      return (
        <div className={hasError ? pillErrorClass : pillClass}>
          {t('segment_creation.select_states')}{hasError ? '?' : ''}
        </div>
      )
    if (filter.values.length === 1) {
      const state = indianStates.find(s => s.key === filter.values[0])
      return (
        <div className={pillClass}>
          {state?.value}
        </div>
      )
    }
    const states = filter.values.map(key => indianStates.find(s => s.key === key)?.value)
    return (
      <div className='flex items-center gap-1 flex-wrap'>
        {states.map(state => (
          <div key={state} className={pillClass}>{state}</div>
        ))}
      </div>
    )
  }, [filter.values, indianStates, t, hasError])

  const prefix = isFirst && !hasFileAbove ? t('segment_creation.from') : t('segment_creation.and')

  return (
    <div className='flex items-start gap-3 py-2.5 border-b-1 border-zinc-950/5'>
      {/* Prefix (from/and) */}
      <div className='w-8 shrink-0'>
        <div className='h-[26px] flex items-center'>
          <span className='text-text-sm-medium text-zinc-500'>{prefix}</span>
        </div>
      </div>

      {/* Content */}
      <div className='flex-1 flex flex-col gap-1'>
        {/* State Badge */}
        <div className='flex items-center gap-1 px-1 py-0.5 border-l-1 border-r-1 border-zinc-950/10 rounded-md w-fit'>
          <MapPinLine size={14} className='text-gray-400' />
          <span className='text-text-sm-medium text-coal-dark'>{t('segment_creation.state')}</span>
        </div>

        {/* Mode and Value selectors */}
        <div className='flex items-start gap-1'>
          {/* Mode Selector (Include / Include all, except) */}
          <GenericSelectionModal
            options={availableInclusionOptions}
            selectedKey={filter.type}
            onSelect={option => onModeChange(index, option.key as SegmentInclusionType)}
            placeholderComponent={
              <div className='flex whitespace-nowrap cursor-pointer items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-zinc-600 transition-colors bg-zinc-950/5 text-coal-dark hover:bg-zinc-950/8'>
                {ALL_INCLUSION_OPTIONS.find(option => option.key === filter.type)?.value}
              </div>
            }
            showSearch={false}
              triggerClassName='!border-0'
              contentClassName='w-48'
            />

            {/* Value Selector (States) - Using GenericMultiSelectDialog */}
            <GenericMultiSelectDialog
              items={statesWithDisabled}
              selectedKeys={filter.values}
              onSelectionChange={keys => onStateChange(index, keys)}
              placeholderComponent={getSelectedStatesDisplay}
              showSearch={true}
              searchPlaceholder={t('segment_creation.search_states')}
              showAddButton={true}
            addButtonText={t('segment_creation.done')}
            triggerClassName='!border-0 !px-0'
            contentClassName='w-72'
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className='flex items-center shrink-0'>
        <IconButton IconComponent={X} size='xs' variant='ghost' onClick={() => onRemove(index)} />
      </div>
    </div>
  )
}

export default StateFilterItem
